<template>
  <!-- Video Section -->
  <div v-if="(isMobile && item.phone_mp4_video) || item.desktop_mp4_video" :class="css"  @click.stop>
    <video
      class="hero-homepage__video-media"
      playsinline
      preload="auto"
      :muted="videoMuted"
      :autoplay="videoPlaying"
      :loop="item.loop_phone_video || item.loop_desktop_video"
    >
      <source :src="isMobile ? item.phone_mp4_video.url : item.desktop_mp4_video.url" type="video/mp4" />
      Sorry, your browser doesn't support embedded videos.
    </video>

    <!-- Controls -->
    <div class="hero-homepage__controls">
      <button
        type="button"
        class="hero-homepage__sounds-control"
        :aria-pressed="!videoMuted"
        :aria-label="videoMuted ? 'Unmute video' : 'Mute video'"
        @click="toggleMute"
        @keydown.enter.prevent="toggleMute"
        @keydown.space.prevent="toggleMute"
      >
        <svg v-if="!videoMuted" id="Sounds_On" data-name="Sounds On" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 183.18712 183.18712" aria-hidden="true">
          <path d="M104.17854,35.93975V147.24737a4.03673,4.03673,0,0,1-6.77655,2.96454L73.45155,128.07677a39.57844,39.57844,0,0,0-26.8631-10.51245H29.14833a4.29163,4.29163,0,0,1-4.29163-4.29168V69.91448a4.29163,4.29163,0,0,1,4.29163-4.29169H46.58845a39.57872,39.57872,0,0,0,26.8631-10.51245L97.402,32.97521A4.03673,4.03673,0,0,1,104.17854,35.93975ZM121.897,150.42485a65.73223,65.73223,0,0,0,.00114-117.66322,6.64151,6.64151,0,0,0-5.91662,11.89252,52.44988,52.44988,0,0,1,.00114,93.87818,6.641,6.641,0,1,0,5.91434,11.89252Zm.0627-31.07879a37.2478,37.2478,0,0,0,0-55.50507,6.38018,6.38018,0,1,0-8.65491,9.37653,24.49352,24.49352,0,0,1,0,36.752,6.38018,6.38018,0,0,0,8.65491,9.37653Z"/>
        </svg>

        <svg v-if="videoMuted" id="Sounds_Off" data-name="Sounds Off" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 183.18712 183.18712" aria-hidden="true">
          <path d="M113.30482,73.21752a6.38018,6.38018,0,1,1,8.65491-9.37653,37.50418,37.50418,0,0,1,12.40436,27.75165,36.74966,36.74966,0,0,1-1.45484,10.25263l-11.3341-11.33405A24.96148,24.96148,0,0,0,113.30482,73.21752Zm2.6767-28.56335a52.48146,52.48146,0,0,1,25.561,65.82446l10.07709,10.07709a65.76794,65.76794,0,0,0-29.7215-87.79413,6.64153,6.64153,0,0,0-5.91662,11.89258Zm-11.803,28.46038V35.93975A4.03673,4.03673,0,0,0,97.402,32.97521L80.06349,48.9995Zm-48.5647-29.97c-.04834-.04828-.08941-.10089-.13592-.15033l-9.14753-9.14752a6.57423,6.57423,0,1,0-9.29687,9.29785L57.8647,63.97576a39.561,39.561,0,0,1-11.27625,1.647H29.14833a4.29163,4.29163,0,0,0-4.29163,4.29169v43.35816a4.29163,4.29163,0,0,0,4.29163,4.29168H46.58845a39.57844,39.57844,0,0,1,26.8631,10.51245L97.402,150.21191a4.03673,4.03673,0,0,0,6.77655-2.96454V110.2896l21.76263,21.76264a52.0402,52.0402,0,0,1-9.95849,6.4801,6.641,6.641,0,1,0,5.91437,11.89252,65.35123,65.35123,0,0,0,13.47412-8.94263l7.84832,7.84833a6.57063,6.57063,0,0,0,9.43329-9.147Z"/>
        </svg>
      </button>

      <button
        type="button"
        class="hero-homepage__play-control"
        @click="togglePlay"
        @keydown.enter.prevent="togglePlay"
        @keydown.space.prevent="togglePlay"
        :aria-pressed="videoPlaying"
        :aria-label="videoPlaying ? 'Pause video' : 'Play video'"
      >
        <svg
          v-if="!videoPlaying"
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M8 5.14v14l11-7l-11-7z" fill="currentColor" />
        </svg>

        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="30"
          height="30"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M14 19h4V5h-4M6 19h4V5H6v14z" fill="currentColor" />
        </svg>
      </button>

    </div>
  </div>

  <!-- Picture Section -->
  <picture v-else :class="css">
    <source :media="`(min-width: ${mobileBreakpointWidth})`" :srcset="desktopSrcSet" :sizes="desktopSizes" />
    <source :srcset="mobileSrcSet" :sizes="mobileSizes" />
    <img v-if="!isMobile" class="hero-homepage__picture--image-desktop" :loading="imgLoadingDesktop" :src="desktopImage.src" :width="1440" :height="500" role="presentation" />
    <img v-else class="hero-homepage__picture--image-mobile" :loading="imgLoadingMobile" :src="mobileImage.src" :width="mobileImage.width" :height="mobileImage.height" role="presentation" />
  </picture>
</template>

<script>
import { mobileBreakpoint } from "Assets/js/components/size-checker";

export default {
  name: "heroHomepagePicture",
  props: {
    main: Boolean,
    initialLoad: Boolean,
    active: Boolean,
    desktopSizes: String,
    mobileSizes: String,
    item: Object,
    translations: Object,
    isMobile: Boolean,
  },
  data() {
    return {
      videoMuted: true,
      videoPlaying: true,
      lazyImg: this.active,
    };
  },
  computed: {
    css() {
      return [
        'hero-homepage__picture',
        this.active && 'hero-homepage__picture--active',
        this.initialLoad && 'hero-homepage__picture--initial',
        this.item.desktop_mp4_video  && 'hero-homepage__picture--video',
        this.item.phone_mp4_video  && 'hero-homepage-mobile__picture--video',
        this.item.colour_filter && this.item.colour_filter !== 'none' && `hero-homepage__picture--filter hero-homepage__picture--filter-${this.item.colour_filter}`,
      ].filter(Boolean).join(' ');
    },
    imgLoadingDesktop() {
      return !this.isMobile && this.active ? 'eager' : 'lazy';
    },
    imgLoadingMobile() {
      return this.isMobile && this.active ? 'eager' : 'lazy';
    },
    desktopSrcSet() {
      return this.lazyImg ? this.item.desktop_image?.srcset || '' : '';
    },
    mobileSrcSet() {
      return this.lazyImg ? this.item.mobile_image?.srcset || '' : '';
    },
    desktopImage() {
      return this.lazyImg ? this.item.desktop_image || {} : {};
    },
    mobileImage() {
      return this.lazyImg ? this.item.mobile_image || {} : {};
    },
    mobileBreakpointWidth() {
      return `${mobileBreakpoint()}px`;
    },
  },
  updated() {
    const video = this.$el.querySelector('video');
    if (video) {
      this.videoPlaying ? video.play() : video.pause();
    } else {
      this.lazyImg = true;
    }
  },
  methods: {
    toggleMute() {
      console.log('toggleMute');
      this.videoMuted = !this.videoMuted;
    },
    togglePlay() {
      console.log('togglePlay');
      this.videoPlaying = !this.videoPlaying;
      const video = this.$el.querySelector('video');
      if (video) {
        this.videoPlaying ? video.play() : video.pause();
      }
    },
    t(key) {
      return this.translations?.[key] || "";
    }
  }
};
</script>
